import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@southfields-digital/mpxlive-components';

import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_SPORT_DATA_MATCH_STATS } from 'src/redux/reducers/sportData';
import { getCards, getMatchStatsFromStat2, passPct, pct, val } from './utils';

import SportDataInput from '../SportDataInput';
import StatRow, { StatRowItem } from './StatRow';

import styles from './MatchStats.module.scss';

type Props = {
  handleImportData: (value: string | PropertyValue[]) => void;
  selectedProperty?: Properties;
};

export default function MatchStats({ handleImportData, selectedProperty }: Props) {
  const dispatch = useDispatch();

  const { matchData } = useSelector((state: StateType) => state.sportData);
  const [fixtureId, setFixtureId] = useState<string>(matchData.data?.fixtureId ?? '');

  useEffect(() => {
    if (!fixtureId) return;

    dispatch({
      type: REQUEST_GET_SPORT_DATA_MATCH_STATS,
      payload: { fx: fixtureId },
    });
  }, [dispatch, fixtureId]);

  if (!matchData.data?.fixtureData?.liveData) {
    return <SportDataInput fixtureId={fixtureId} onChange={setFixtureId} />;
  }

  const {
    matchInfo,
    liveData: { lineUp, card },
  } = matchData.data.fixtureData;

  const [homeTeam, awayTeam] = matchInfo.contestant;
  const [homeLineUp, awayLineUp] = lineUp || [];

  const [yellowHome, redHome] = getCards(card, homeTeam.id);
  const [yellowAway, redAway] = getCards(card, awayTeam.id);

  const homeStats = getMatchStatsFromStat2(homeLineUp?.stat);
  const awayStats = getMatchStatsFromStat2(awayLineUp?.stat);

  const isRepeater = selectedProperty?.type === 'repeater';

  const items: StatRowItem[] = [
    {
      label: 'Ball Possession',
      homeValue: pct(homeStats.possessionPercentage),
      awayValue: pct(awayStats.possessionPercentage),
    },
    {
      label: 'Total Attempts',
      homeValue: val(homeStats.totalScoringAtt),
      awayValue: val(awayStats.totalScoringAtt),
    },
    {
      label: 'Attempts on Target',
      homeValue: val(homeStats.ontargetScoringAtt),
      awayValue: val(awayStats.ontargetScoringAtt),
    },
    {
      label: 'Corners',
      homeValue: val(homeStats.cornerTaken),
      awayValue: val(awayStats.cornerTaken),
    },
    {
      label: 'Offsides',
      homeValue: val(homeStats.totalOffside),
      awayValue: val(awayStats.totalOffside),
    },
    {
      label: 'Passes Completed',
      homeValue: passPct(homeStats.accuratePass, homeStats.totalPass),
      awayValue: passPct(awayStats.accuratePass, awayStats.totalPass),
    },
    {
      label: 'Fouls Committed',
      homeValue: val(homeStats.fkFoulLost),
      awayValue: val(awayStats.fkFoulLost),
    },
    {
      label: 'Yellow Cards',
      homeValue: val(yellowHome?.toString()),
      awayValue: val(yellowAway?.toString()),
    },
    {
      label: 'Red Cards',
      homeValue: val(redHome?.toString()),
      awayValue: val(redAway?.toString()),
    },
  ];

  const handleSingleImport = !isRepeater ? handleImportData : undefined;

  const handleRepeaterImport = () =>
    handleImportData(
      items.map<PropertyValue>((item) => ({
        IdentRowHome: item.homeValue,
        IdentRowCenter: item.label,
        IdentRowAway: item.awayValue,
      }))
    );

  const rowChildren = isRepeater && (
    <Button onClick={handleRepeaterImport} variant="tertiary">
      Import
    </Button>
  );

  return (
    <div className={styles.Container}>
      <SportDataInput fixtureId={fixtureId} onChange={setFixtureId} />
      <div className={styles.Content}>
        <StatRow
          label=""
          homeValue={homeTeam.officialName}
          awayValue={awayTeam.officialName}
          onClickImport={handleSingleImport}
        >
          {rowChildren}
        </StatRow>
        {items.map((item) => (
          <StatRow
            key={item.label}
            label={item.label}
            homeValue={item.homeValue}
            awayValue={item.awayValue}
            onClickImport={handleSingleImport}
          />
        ))}
      </div>
    </div>
  );
}
